// Nav mesh generation from Krunker v7.1.4 map JSON.
//
// Pure data transform: no engine, rendering, or browser globals. Usable from the
// game server, bot worker processes, or any tooling.
//
// Map object decoding (verified against src/data/map.js + MapManager.js):
//   - o.p   = [x, y, z] world position (object center on x/z; y is the BASE on y).
//   - o.si  = index into the top-level `xyz` size pool; size = xyz.slice(si*3, si*3+3)
//             as [sizeX, sizeY, sizeZ] FULL extents. map.js expands the pool
//             straight into o.s with no scaling, then MapManager.addBlock builds
//             the collision body as w: s[0]/2, l: s[2]/2, h: s[1]/2. So the box
//             spans x±s[0]/2, z±s[2]/2, and [y, y + s[1]] vertically.
//   - o.i   = prefab index; prefab 0 = CUBE, 4 = PLANE, 9 = RAMP, 3 = LADDER
//             (see prefabs.js).
//   - o.l / o.col = non-colliding flag (map.js: `noCol: v12622.l || v12622.col`).
//                   These objects are visual-only and do NOT block movement.
//   - o.nai = noAICol; explicitly excluded from AI navigation collision.
//
// Solid CUBEs, PLANEs, and RAMPs contribute to the grid. CUBEs block; solid CUBE
// and PLANE tops define flat walkable surfaces; RAMP surfaces are sampled by
// their progress along the engine's ramp start/end line so cells on the ramp
// form intermediate walkable heights instead of snapping to the top. A cell is
// walkable if it has a support surface and enough vertical clearance above it
// for a player to stand.

export interface MapObjectRaw {
  p: [number, number, number];
  i?: number;
  id?: number;
  si?: number;
  s?: [number, number, number];
  d?: number;
  l?: number;
  col?: number;
  nai?: number;
  bo?: number;
  ns?: number; // noShoot: bullets/sight pass through (engine canSee skip)
  o?: number; // opacity; < 1 means transparent (engine treats as see-through)
  cpx?: number; // complexColl: engine canSee skips these broad AABBs
}

export interface MapJson {
  objects?: MapObjectRaw[];
  xyz?: number[];
  spawns?: number[][];
  name?: string;
}

// Prefab indices we care about for navigation (from prefabs.js dump).
const PREFAB_CUBE = 0;
const PREFAB_CRATE = 1;
const PREFAB_BARREL = 2;
const PREFAB_LADDER = 3;
const PREFAB_PLANE = 4;
const PREFAB_RAMP = 9;

// Prefabs that MapManager inserts as trigger/helper GameObjects with noShoot set
// internally even when the raw map object has no `ns` field.
const ENGINE_NO_SHOOT_PREFABS = new Set([
  3, // LADDER
  5, // SPAWN_POINT
  10, // SCORE_ZONE
  12, // DEATH_ZONE
  14, // OBJECTIVE
  25, // CHECK_POINT
  27, // TELEPORTER
  42, // PREMIUM_ZONE
  43, // VERIFIED_ZONE
  46, // BOOST_PAD
  69, // KR_ZONE
  70, // NOPVP_ZONE
  75, // PARTNER_ZONE
  77, // DOMINATION_ZONE
  78, // ROPE_LADDER
  80, // KPD_ZONE
]);

// Environmental prefabs whose simple map extents should block line of sight.
// Movement collision is still intentionally narrower in extractBoxes().
const SIGHT_GEOMETRY_PREFABS = new Set([
  PREFAB_CUBE,
  PREFAB_CRATE,
  PREFAB_BARREL,
  PREFAB_PLANE,
  PREFAB_RAMP,
  7, // VEHICLE
  8, // STACK
  11, // BILLBOARD
  15, // TREE
  16, // CONE
  17, // CONTAINER
  19, // CONTAINERR
  20, // ACIDBARREL
  21, // DOOR
  24, // GATE
  28, // TEDDY
  30, // SIGN
  31, // DEPOSIT_BOX
  34, // SPHERE
  36, // CARDB
  37, // PALLET
  41, // TERMINAL
  44, // CUSTOM_ASSET
  48, // CYLINDER
  49, // POLICE
  50, // CAGE
  51, // EBARREL
  54, // GHOST
  56, // PUMPKIN
  58, // SKELETON
  59, // KNIGHT
  60, // GIFT
  61, // CHRISTMASTREE
  68, // PRIZE_WHEEL
  73, // TILES
  81, // DUMPSTER
  83, // LAMP
  85, // AC_UNIT
  87, // ANTENNA
  88, // SATELLITE_DISH
  90, // WEAPONBOX
  91, // MYSTERY_BOX
  92, // PERK_MACHINE
  94, // KRUM_MACHINE
]);

// Tuning. All values are grounded in the engine's own physics constants
// (src/config.js, consumed by src/data/player.js procInputs/collision), not
// invented:
//   - playerHeight  = config.playerHeight (11): the standing body span used for
//                     headroom/clearance checks.
//   - stepHeight    = config.climbHeight (3): a surface higher than this above
//                     the player can't be walked onto -- it's a wall to climb,
//                     not a step. This is the inter-layer connection threshold.
//   - playerRadius  = config.playerScale (2.45) + config.collPad (1.1) = 3.55:
//                     the horizontal half-extent the movement collision path
//                     tests with (`this.scale + hbS`) plus the engine's small
//                     collision pad. Do not use hitBoxPad here; that is for
//                     damage/hit testing and makes tight door corridors vanish.
//   - floorTolerance: small epsilon for coplanar-surface dedup / "top is floor".
export interface NavMeshConfig {
  cellSize: number; // world units per grid cell
  playerHeight: number; // vertical body span a standing player occupies (world units)
  stepHeight: number; // max walkable height step between adjacent layers (climbHeight)
  playerRadius: number; // horizontal collision half-extent (scale + hitBoxPad)
  floorTolerance: number; // how far below a box top still counts as the floor surface
  padding: number; // walkable-bounds padding so edges aren't clipped
  minComponentCells: number; // keep non-spawn components at least this large
  jumpHeight: number; // max upward ledge/box height bots may attempt with jump
  ladderReach: number; // horizontal reach around ladder volumes for graph links
}

// Engine constants (src/config.js):
//   playerHeight 11, climbHeight 3, playerScale 2.45, collPad 1.1.
export const DEFAULT_NAVMESH_CONFIG: NavMeshConfig = {
  cellSize: 12,
  playerHeight: 11,
  stepHeight: 3,
  playerRadius: 5.2,
  floorTolerance: 1,
  padding: 24,
  minComponentCells: 16,
  jumpHeight: 18,
  ladderReach: 16,
};

export interface SolidBox {
  minX: number;
  maxX: number;
  minZ: number;
  maxZ: number;
  baseY: number; // bottom
  topY: number; // top
  ramp: boolean;
  surface: boolean;
  rampStartX?: number;
  rampStartZ?: number;
  rampEndX?: number;
  rampEndZ?: number;
}

export interface LadderRegion {
  minX: number;
  maxX: number;
  minZ: number;
  maxZ: number;
  baseY: number;
  topY: number;
  dir: number;
}

export interface NavPoint {
  x: number;
  y: number;
  z: number;
}

function prefabOf(o: MapObjectRaw): number {
  return o.i ?? o.id ?? 0;
}

// True when the engine's canSee() would let a line of sight pass through this
// object. Mirrors src/data/game.js canSee: an object blocks sight unless it is
// noShoot (o.ns) or transparent (opacity o.o < 1). Note: noCol (o.l / o.col)
// and noAICol (o.nai) do NOT exempt an object from blocking sight/bullets, so
// they are intentionally ignored here.
function isSightTransparent(o: MapObjectRaw): boolean {
  if (o.ns != null) return true;
  const opacity = Number(o.o);
  return Number.isFinite(opacity) && opacity !== 1;
}

function blocksSightByPrefab(o: MapObjectRaw): boolean {
  if (o.cpx != null) return false;
  const prefab = prefabOf(o);
  if (ENGINE_NO_SHOOT_PREFABS.has(prefab)) return false;
  return SIGHT_GEOMETRY_PREFABS.has(prefab);
}

function isNonColliding(o: MapObjectRaw): boolean {
  return o.l != null || o.col != null || o.nai != null;
}

function sizeOf(o: MapObjectRaw, xyz: number[]): [number, number, number] {
  if (o.si != null) {
    const b = o.si * 3;
    return [xyz[b] || 0, xyz[b + 1] || 0, xyz[b + 2] || 0];
  }
  if (Array.isArray(o.s)) return o.s;
  return [0, 0, 0];
}

// Extract solid (collision-contributing) boxes from the map.
export function extractBoxes(map: MapJson): SolidBox[] {
  const xyz = map.xyz || [];
  const objs = map.objects || [];
  const boxes: SolidBox[] = [];
  for (const o of objs) {
    if (!Array.isArray(o.p)) continue;
    if (isNonColliding(o)) continue;
    const prefab = prefabOf(o);
    const ramp = prefab === PREFAB_RAMP;
    const plane = prefab === PREFAB_PLANE;
    const surface = prefab === PREFAB_CUBE || plane || ramp;
    const blockerOnly = prefab === PREFAB_CRATE || prefab === PREFAB_BARREL;
    if (!surface && !blockerOnly) continue;
    // o.s holds FULL extents on all three axes (verified: map.js expands the
    // xyz pool straight into o.s with no scaling, then MapManager.addBlock
    // builds the collision body as w: s[0]/2, l: s[2]/2, h: s[1]/2 with y as the
    // base). So the box spans [x - s[0]/2, x + s[0]/2] on x,
    // [z - s[2]/2, z + s[2]/2] on z, and [y, y + s[1]] vertically (y is the
    // base, not the center).
    const [sx, sy, sz] = sizeOf(o, xyz);
    const halfW = sx / 2;
    const halfL = sz / 2;
    if (halfW <= 0 || halfL <= 0 || (!plane && sy <= 0)) continue;
    const [x, y, z] = o.p;
    const height = plane ? Math.max(sy, 0.01) : sy;
    const dir = (Number(o.d) || 0) * Math.PI / 2;
    const rampRotated = dir !== 0 && dir !== Math.PI;
    const rampAxisHalf = (rampRotated ? sz : sx) / 2;
    boxes.push({
      minX: x - halfW,
      maxX: x + halfW,
      minZ: z - halfL,
      maxZ: z + halfL,
      baseY: y,
      topY: y + height,
      ramp,
      surface,
      rampStartX: ramp ? x - rampAxisHalf * Math.cos(dir) : undefined,
      rampStartZ: ramp ? z - rampAxisHalf * Math.sin(dir) : undefined,
      rampEndX: ramp ? x + rampAxisHalf * Math.cos(dir) : undefined,
      rampEndZ: ramp ? z + rampAxisHalf * Math.sin(dir) : undefined,
    });
  }
  return boxes;
}

// Extract sight-blocking boxes for line-of-sight tests, mirroring the engine's
// canSee() (src/data/game.js): a box blocks sight unless it is noShoot or
// transparent. Unlike extractBoxes (movement collision), this intentionally
// KEEPS noCol (l/col) and noAICol (nai) objects, because those still stop
// bullets and sight in the real engine. Using movement-collision boxes for LOS
// is what let bots shoot enemies through noCol/noAICol walls.
export function extractSightBoxes(map: MapJson): SolidBox[] {
  const xyz = map.xyz || [];
  const objs = map.objects || [];
  const boxes: SolidBox[] = [];
  for (const o of objs) {
    if (!Array.isArray(o.p)) continue;
    if (isSightTransparent(o)) continue;
    const prefab = prefabOf(o);
    const ramp = prefab === PREFAB_RAMP;
    const plane = prefab === PREFAB_PLANE;
    const surface = prefab === PREFAB_CUBE || plane || ramp;
    if (!blocksSightByPrefab(o)) continue;
    const [sx, sy, sz] = sizeOf(o, xyz);
    const halfW = sx / 2;
    const halfL = sz / 2;
    if (halfW <= 0 || halfL <= 0 || (!plane && sy <= 0)) continue;
    const [x, y, z] = o.p;
    const height = plane ? Math.max(sy, 0.01) : sy;
    const dir = (Number(o.d) || 0) * Math.PI / 2;
    const rampRotated = dir !== 0 && dir !== Math.PI;
    const rampAxisHalf = (rampRotated ? sz : sx) / 2;
    boxes.push({
      minX: x - halfW,
      maxX: x + halfW,
      minZ: z - halfL,
      maxZ: z + halfL,
      baseY: y,
      topY: y + height,
      ramp,
      surface,
      rampStartX: ramp ? x - rampAxisHalf * Math.cos(dir) : undefined,
      rampStartZ: ramp ? z - rampAxisHalf * Math.sin(dir) : undefined,
      rampEndX: ramp ? x + rampAxisHalf * Math.cos(dir) : undefined,
      rampEndZ: ramp ? z + rampAxisHalf * Math.sin(dir) : undefined,
    });
  }
  return boxes;
}

// Extract climbable ladder volumes. Ladders do not act as normal blocking
// surfaces for the nav grid, but the bot graph uses these regions to add
// vertical links between nearby low/high walkable layers.
export function extractLadders(
  map: MapJson,
  cfg: Pick<NavMeshConfig, "ladderReach"> = DEFAULT_NAVMESH_CONFIG,
): LadderRegion[] {
  const xyz = map.xyz || [];
  const objs = map.objects || [];
  const ladders: LadderRegion[] = [];
  for (const o of objs) {
    if (!Array.isArray(o.p)) continue;
    if (isNonColliding(o)) continue;
    if (prefabOf(o) !== PREFAB_LADDER) continue;
    const [sx, sy, sz] = sizeOf(o, xyz);
    if (sx <= 0 || sz <= 0 || sy <= 0) continue;
    const [x, y, z] = o.p;
    const dir = (Number(o.d) || 0) * Math.PI / 2;
    const centerX = x + 0.5 * Math.cos(dir);
    const centerZ = z + 0.5 * Math.sin(dir);
    const halfX = sx / 2 + cfg.ladderReach;
    const halfZ = sz / 2 + cfg.ladderReach;
    ladders.push({
      minX: centerX - halfX,
      maxX: centerX + halfX,
      minZ: centerZ - halfZ,
      maxZ: centerZ + halfZ,
      baseY: y,
      topY: y + sy,
      dir,
    });
  }
  return ladders;
}

function progressOnLine(
  sx: number,
  sz: number,
  ex: number,
  ez: number,
  x: number,
  z: number,
): number {
  const dx = ex - sx;
  const dz = ez - sz;
  const lenSq = dx * dx + dz * dz;
  if (lenSq <= 0) return 0;
  return Math.max(0, Math.min(1, ((x - sx) * dx + (z - sz) * dz) / lenSq));
}

function surfaceYAt(box: SolidBox, x: number, z: number): number {
  if (
    box.ramp &&
    box.rampStartX != null &&
    box.rampStartZ != null &&
    box.rampEndX != null &&
    box.rampEndZ != null
  ) {
    const progress = progressOnLine(
      box.rampStartX,
      box.rampStartZ,
      box.rampEndX,
      box.rampEndZ,
      x,
      z,
    );
    return box.baseY + (box.topY - box.baseY) * progress;
  }
  return box.topY;
}

function segmentIntersectsAabb(
  from: NavPoint,
  to: NavPoint,
  minX: number,
  minY: number,
  minZ: number,
  maxX: number,
  maxY: number,
  maxZ: number,
): boolean {
  let near = 0;
  let far = 1;
  const axes = [
    [from.x, to.x - from.x, minX, maxX],
    [from.y, to.y - from.y, minY, maxY],
    [from.z, to.z - from.z, minZ, maxZ],
  ];

  for (const [origin, direction, min, max] of axes) {
    if (Math.abs(direction) < 1e-8) {
      if (origin < min || origin > max) return false;
      continue;
    }
    let axisNear = (min - origin) / direction;
    let axisFar = (max - origin) / direction;
    if (axisNear > axisFar) {
      const tmp = axisNear;
      axisNear = axisFar;
      axisFar = tmp;
    }
    near = Math.max(near, axisNear);
    far = Math.min(far, axisFar);
    if (near > far) return false;
  }

  return far > 0 && near < 1;
}

export function canSeeThroughBoxes(
  boxes: readonly SolidBox[],
  from: NavPoint,
  to: NavPoint,
  includeRamp = false,
  paddingXZ = 0,
): boolean {
  const minSegX = Math.min(from.x, to.x);
  const maxSegX = Math.max(from.x, to.x);
  const minSegY = Math.min(from.y, to.y);
  const maxSegY = Math.max(from.y, to.y);
  const minSegZ = Math.min(from.z, to.z);
  const maxSegZ = Math.max(from.z, to.z);
  for (const box of boxes) {
    if (box.ramp && !includeRamp) continue;
    if (
      box.maxX + paddingXZ < minSegX ||
      box.minX - paddingXZ > maxSegX ||
      box.topY < minSegY ||
      box.baseY > maxSegY ||
      box.maxZ + paddingXZ < minSegZ ||
      box.minZ - paddingXZ > maxSegZ
    ) continue;
    if (
      segmentIntersectsAabb(
        from,
        to,
        box.minX - paddingXZ,
        box.baseY,
        box.minZ - paddingXZ,
        box.maxX + paddingXZ,
        box.topY,
        box.maxZ + paddingXZ,
      )
    ) {
      return false;
    }
  }
  return true;
}

// Swept-box trace returning the fraction [0,1] of the from->to segment that is
// clear before the first solid box is hit (1 = no hit, 0 = blocked at the
// start / inside a solid). This mirrors Source's trace_t.fraction so callers can
// do proportional steering (lever's PathFollower::Avoid uses 1-fraction to push
// harder the deeper a feeler is in a wall) instead of a clear/blocked boolean.
export function sweepBoxesFraction(
  boxes: readonly SolidBox[],
  from: NavPoint,
  to: NavPoint,
  includeRamp = false,
  paddingXZ = 0,
): number {
  const minSegX = Math.min(from.x, to.x);
  const maxSegX = Math.max(from.x, to.x);
  const minSegY = Math.min(from.y, to.y);
  const maxSegY = Math.max(from.y, to.y);
  const minSegZ = Math.min(from.z, to.z);
  const maxSegZ = Math.max(from.z, to.z);
  let nearest = 1;
  for (const box of boxes) {
    if (box.ramp && !includeRamp) continue;
    if (
      box.maxX + paddingXZ < minSegX ||
      box.minX - paddingXZ > maxSegX ||
      box.topY < minSegY ||
      box.baseY > maxSegY ||
      box.maxZ + paddingXZ < minSegZ ||
      box.minZ - paddingXZ > maxSegZ
    ) continue;
    const t = segmentAabbEntryFraction(
      from,
      to,
      box.minX - paddingXZ,
      box.baseY,
      box.minZ - paddingXZ,
      box.maxX + paddingXZ,
      box.topY,
      box.maxZ + paddingXZ,
    );
    if (t != null && t < nearest) nearest = t;
  }
  return nearest;
}

// Entry fraction of the from->to segment into the AABB, or null if it never
// overlaps. Same slab math as segmentIntersectsAabb but returns the clamped
// entry parameter (a start-solid hit returns 0).
function segmentAabbEntryFraction(
  from: NavPoint,
  to: NavPoint,
  minX: number,
  minY: number,
  minZ: number,
  maxX: number,
  maxY: number,
  maxZ: number,
): number | null {
  let near = 0;
  let far = 1;
  const axes = [
    [from.x, to.x - from.x, minX, maxX],
    [from.y, to.y - from.y, minY, maxY],
    [from.z, to.z - from.z, minZ, maxZ],
  ];

  for (const [origin, direction, min, max] of axes) {
    if (Math.abs(direction) < 1e-8) {
      if (origin < min || origin > max) return null;
      continue;
    }
    let axisNear = (min - origin) / direction;
    let axisFar = (max - origin) / direction;
    if (axisNear > axisFar) {
      const tmp = axisNear;
      axisNear = axisFar;
      axisFar = tmp;
    }
    near = Math.max(near, axisNear);
    far = Math.min(far, axisFar);
    if (near > far) return null;
  }

  if (far <= 0 || near >= 1) return null;
  return Math.max(0, near);
}
